import React, { useEffect, useState } from "react";
import Layout from "../../../layouts/default";
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchViCtsListViewData,
  getPromotionalContactsDispatcher,
  initiateFileDownloadThunk,
} from "../../../store/slices/viCts/viCts";
import SmartPagination from "../../Report/EmailCampaignRunReport/DynamicSmartPagination";
import {
  convertToIndianDateTime,
  getStatusBadge,
  hasPermission,
} from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";

function ListView() {
  const dispatch = useDispatch();
  const [promotionalContactLoading, setPromotionalContactLoading] =
    useState(false);

  const { list, loading, pagination } = useSelector(
    (store) => store.viCtsReducer.listView,
  );

  const [filters, setFilters] = useState({
    batchId: "",
    date: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const fetchData = async (page = 1, appliedFilters = filters) => {
    try {
      await dispatch(
        fetchViCtsListViewData({
          page,
          limit,
          filters: appliedFilters,
        }),
      );
    } catch (err) {
      toast.error(err?.message || "Internal Server Error!");
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchData(1, filters);
  };

  const handleClearFilters = () => {
    const cleared = { batchId: "", date: "" };
    setFilters(cleared);
    setCurrentPage(1);
    fetchData(1, cleared);
  };

  const handleGetPromotionalData = async () => {
    try {

      if (
        !window.confirm(
          "Are you sure you want to proceed? This will upload promotional data to VI-CTS in incremental order based on the configured settings."
        )
      ) {
        return;
      }

      setPromotionalContactLoading(true);
      const msg = await dispatch(
        getPromotionalContactsDispatcher(),
      ).unwrap();
      toast.success(msg);
      setCurrentPage(1);
      fetchData(1);
    } catch (err) {
      toast.error(err || err?.message || "Internal Server Error!");
    } finally {
      setPromotionalContactLoading(false);
    }
  };

  const handleDownloadFile = async (data, fileSource) => {
    try {
      const msg = await dispatch(
        initiateFileDownloadThunk({ ...data, fileSource }),
      ).unwrap();
      toast.success(msg);
    } catch (err) {
      toast.error(err?.message || "Internal Server Error!");
    }
  };

  if (!hasPermission(PERMISSIONS.VI_CTS.VIEW)) {
    return (
      <Layout>
        <div>
          <h3>You Do not have permission to view this screen</h3>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-semibold mb-0">VI-CTS List View</h4>
        {
          hasPermission(PERMISSIONS.VI_CTS.EDIT) && <Button
            disabled={promotionalContactLoading}
            variant="primary"
            onClick={handleGetPromotionalData}
          >
            {promotionalContactLoading
              ? "Loading..."
              : "Get Promotional Contact Batch"}
          </Button>
        }
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="align-items-end g-2">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Batch ID</Form.Label>
              <Form.Control
                type="text"
                name="batchId"
                value={filters.batchId}
                placeholder="Search by Batch ID"
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                name="date"
                value={filters.date}
                max={new Date().toISOString().split("T")[0]}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col md={4} className="text-end">
            <Button
              variant="primary"
              className="me-2"
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>
            <Button variant="secondary" onClick={handleClearFilters}>
              Clear
            </Button>
          </Col>
        </Row>
      </div>

      {/* Table */}
      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Batch ID</th>
            <th>Created At</th>
            <th>Status</th>
            <th>Records</th>
            <th>Requested By</th>
            {
              hasPermission(PERMISSIONS.VI_CTS.DOWNLOAD) && <th className="text-center">Downloads</th>
            }
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={8} className="text-center">
                <Spinner animation="border" size="sm" /> Loading...
              </td>
            </tr>
          ) : list?.length ? (
            list.map((item, index) => (
              <tr key={item.batchId}>
                <td>{(currentPage - 1) * limit + index + 1}</td>

                <td>
                  <Badge bg="secondary">{item.batchId}</Badge>
                </td>

                <td>
                  {convertToIndianDateTime(item.createdAt) || "NA"}
                </td>

                <td>{getStatusBadge(item.status)}</td>
                <td>
                  <div>
                    <Badge bg="dark" className="me-1">
                      Total: {item.totalRecords}
                    </Badge>
                    <Badge bg="primary" className="me-1">
                      CQ: {item.cqRecords}
                    </Badge>
                    <Badge bg="success">
                      FT: {item.ftRecords}
                    </Badge>
                  </div>
                </td>

                <td>
                  {item?.requestedBy?.name || "NA"}
                </td>

                {
                  hasPermission(PERMISSIONS.VI_CTS.DOWNLOAD) && <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() =>
                        handleDownloadFile(item, "cqFile")
                      }
                      disabled={!item.cqRecords}
                    >
                      <FaDownload className="me-1" />
                      CQ
                    </Button>

                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() =>
                        handleDownloadFile(item, "ftFile")
                      }
                      disabled={!item.ftRecords}
                    >
                      <FaDownload className="me-1" />
                      FT
                    </Button>
                  </td>
                }
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} className="text-center">
                No records found!!
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination */}
      {pagination?.totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <SmartPagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Layout>
  );
}

export default ListView;
